/**
 * Property grid card — matches the find-properties.html mockup layout:
 *
 *   ┌─ image (4:3) ───────────────────────────────────┐
 *   │  [Active]  [BRRRR/Flip badge]      [☐ Compare] │
 *   │                                                 │
 *   │                            [♡ heart / ✓ Saved] │
 *   └─────────────────────────────────────────────────┘
 *   $139,900                                  ⌛ 1 days
 *   ⌂ Single Family
 *   915 SHELTON STREET
 *   Birmingham, AL 35215
 *   🛏 3 beds · 🛁 2 baths · 📐 1,727 sqft
 *   ──────────────────────────────────────────────
 *    Est. Rent       Cap Rate ⓘ      CoC Return ⓘ
 *    $1,251          10.7%           3.9%
 */
import { useState } from "react";
import {
  Bath,
  Bed,
  Check,
  Clock,
  Heart,
  Home,
  Loader2,
  RefreshCcw,
  ArrowLeftRight,
  Ruler,
} from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { DomainTermInfo } from "@/components/ui/domain-term-info";
import {
  LISTING_PROPERTY_TYPE_LABELS,
  LISTING_PROPERTY_TYPE_PILL,
  normalizeListingPropertyType,
} from "@/lib/listing-property-type";
import { cn } from "@/lib/utils";
import {
  MockProperty,
  statusColors,
  statusLabels,
  propertyTypeLabels,
} from "@/data/mockProperties";
import { usePropertyComparison } from "@/hooks/use-property-comparison";
import { useSavedProperties } from "@/hooks/use-saved-properties";

interface PropertyCardProps {
  property: MockProperty;
  isSelected?: boolean;
  onClick?: () => void;
  showCompareCheckbox?: boolean;
}

const STRATEGY_BADGE: Record<
  string,
  { label: string; color: string; Icon: typeof RefreshCcw }
> = {
  BRRRR: {
    label: "BRRRR",
    color: "bg-emerald-500/85 text-white",
    Icon: RefreshCcw,
  },
  Flip: {
    label: "Flip",
    color: "bg-amber-500/85 text-white",
    Icon: ArrowLeftRight,
  },
  Rental: {
    label: "Rental",
    color: "bg-cyan-500/85 text-white",
    Icon: Home,
  },
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "";
  return String(s)
    .split(" ")
    .map((word) => {
      // Keep short all-caps tokens (state codes, S/N/E/W) as-is
      if (word.length <= 2 && /^[A-Z]+$/.test(word.replace(/[,.]/g, ""))) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function PropertyCard({
  property,
  isSelected,
  onClick,
  showCompareCheckbox = true,
}: PropertyCardProps) {
  const {
    isSelected: isCompareSelected,
    toggleProperty,
    canAddMore,
  } = usePropertyComparison();
  const { savedProperties, saveProperty } = useSavedProperties();
  const [isSaving, setIsSaving] = useState(false);
  const isInComparison = isCompareSelected(property.id);

  // A pipeline_properties row exists in one of three states for this property:
  //   - missing      → not saved; show the heart button
  //   - favourite    → saved with no pipeline_id; the heart button is replaced
  //                    by a static "Saved" pill, so there's no click target
  //                    left to stray-click the property back off the list
  //   - in pipeline  → saved with a pipeline_id; no save affordance at all, the
  //                    strategy badge already says the row is tracked
  const savedRow = savedProperties.find((p) => p.property_id === property.id);
  const isFavourite = savedRow !== undefined && !savedRow.pipeline_id;

  // Strategy badge appears once the row has been promoted into the pipeline.
  const strategyName =
    (savedRow as unknown as { strategy?: string } | undefined)?.strategy ?? null;
  const strategyBadge = strategyName ? STRATEGY_BADGE[strategyName] : null;

  // Which MLS bucket this listing came out of — Residential / Commercial / Land.
  // Normalized rather than trusted: rows cached before the edge function started
  // sending the field have nothing here, and an unreadable value renders no pill
  // rather than mislabelling a strip mall "Residential".
  const listingType = normalizeListingPropertyType(property.listingPropertyType);

  function handleCompareToggle(e: React.MouseEvent) {
    e.stopPropagation();
    toggleProperty(property);
  }

  // Save only — unsaving is deliberate and lives in the Saved Properties
  // drawer and on the property detail page, not one click away on the grid.
  async function handleSave(e: React.MouseEvent) {
    e.stopPropagation();
    setIsSaving(true);
    await saveProperty(property);
    setIsSaving(false);
  }

  return (
    <div
      className={cn(
        "flex cursor-pointer flex-col overflow-hidden rounded-md border border-border bg-card transition hover:-translate-y-0.5 hover:border-border/80",
        isSelected && "ring-2 ring-primary",
      )}
      onClick={onClick}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted/40">
        {property.imageUrl ? (
          <img
            src={property.imageUrl}
            alt={property.address}
            className="h-full w-full object-cover transition-transform hover:scale-[1.03]"
          />
        ) : null}

        {/* Top-left: Active badge + strategy badge */}
        <div className="absolute left-2 top-2 flex items-center gap-1.5">
          <span
            className={cn(
              "rounded px-2 py-0.5 text-[11px] font-semibold text-white",
              statusColors[property.status],
            )}
          >
            {statusLabels[property.status]}
          </span>
          {strategyBadge && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                strategyBadge.color,
              )}
            >
              <strategyBadge.Icon className="h-3 w-3" aria-hidden />
              {strategyBadge.label}
            </span>
          )}
        </div>

        {/* Top-right: Compare checkbox */}
        {showCompareCheckbox && (
          <label
            className={cn(
              "absolute right-2 top-2 inline-flex items-center gap-1.5 rounded bg-black/65 px-1.5 py-1 text-[11px] text-white backdrop-blur-sm transition",
              !isInComparison && !canAddMore && "opacity-50",
            )}
            onClick={handleCompareToggle}
          >
            <Checkbox
              checked={isInComparison}
              disabled={!isInComparison && !canAddMore}
              className="h-3.5 w-3.5 border-white/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary [&_svg]:h-2.5 [&_svg]:w-2.5"
            />
            <span className="font-medium leading-none">Compare</span>
          </label>
        )}

        {/* Bottom-right: Heart — only while the property is unsaved. */}
        {!savedRow && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            aria-label="Save property"
            className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-foreground shadow-md transition hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-70"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Heart className="h-4 w-4" aria-hidden />
            )}
          </button>
        )}

        {/* …swapped for a read-only "Saved" pill once it is. Not a button, and
            pointer-events-none so a click here still opens the property. */}
        {isFavourite && (
          <div className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full border border-white/25 bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground shadow-md">
            <Check className="h-3.5 w-3.5" aria-hidden />
            Saved
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        {/* Price + days pill */}
        <div className="flex items-center justify-between">
          <div className="text-[22px] font-extrabold leading-none">
            {formatCurrency(property.price)}
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-2.5 py-1 text-[11px]">
            <Clock className="h-3 w-3" aria-hidden />
            {property.daysOnMarket} days
          </span>
        </div>

        {/* Property type + MLS classification pill */}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <Home className="h-3.5 w-3.5" aria-hidden />
            {propertyTypeLabels[property.propertyType]}
          </span>
          {listingType && (
            <span
              className={cn(
                "inline-flex items-center rounded-full border px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-foreground",
                LISTING_PROPERTY_TYPE_PILL[listingType],
              )}
            >
              {LISTING_PROPERTY_TYPE_LABELS[listingType]}
            </span>
          )}
        </div>

        {/* Address */}
        <div className="mt-3">
          <div className="text-[14px] font-bold tracking-wide">
            {titleCase(property.address)}
          </div>
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            {titleCase(property.city)}, {property.state} {property.zip}
          </div>
        </div>

        {/* Specs row */}
        <div className="mt-3 flex flex-wrap items-center gap-3 border-b border-border pb-3 text-[13px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Bed className="h-4 w-4 text-muted-foreground/70" aria-hidden />
            {property.bedrooms} beds
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Bath className="h-4 w-4 text-muted-foreground/70" aria-hidden />
            {property.bathrooms} baths
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Ruler className="h-4 w-4 text-muted-foreground/70" aria-hidden />
            {property.sqft.toLocaleString()} sqft
          </span>
        </div>

        {/* Metrics grid */}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <Metric label="Est. Rent" value={`${formatCurrency(property.estimatedRent)}`} />
          <Metric
            label={
              <>
                Cap Rate <DomainTermInfo termId="CapRate" />
              </>
            }
            value={`${property.capRate}%`}
            tone={
              property.capRate >= 8 ? "pos" : property.capRate < 0 ? "neg" : "dim"
            }
          />
          <Metric
            label={
              <>
                CoC Return <DomainTermInfo termId="CoC" />
              </>
            }
            value={`${property.cashOnCash}%`}
            tone={
              property.cashOnCash >= 12
                ? "pos"
                : property.cashOnCash < 0
                  ? "neg"
                  : "dim"
            }
          />
        </div>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  tone = "dim",
}: {
  label: React.ReactNode;
  value: string;
  tone?: "pos" | "neg" | "dim";
}) {
  return (
    <div>
      <div className="inline-flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-[15px] font-bold",
          tone === "pos" && "text-success",
          tone === "neg" && "text-destructive",
        )}
      >
        {value}
      </div>
    </div>
  );
}
